// Live-link expiry: remaining-time chip + "Refresh link" menu (1 week / 1 month / 12 weeks).
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Hourglass, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export const EXTEND_CHOICES = [
  { weeks: 1, label: "1 week" },
  { weeks: 4, label: "1 month" },
  { weeks: 12, label: "12 weeks" },
];

// "3 weeks remaining" / "5 days remaining" / "Expires today" / "Expired 2 days ago"
export function fmtRemaining(daysRemaining) {
  if (daysRemaining == null) return null;
  if (daysRemaining <= 0) {
    const ago = Math.abs(daysRemaining);
    return ago === 0 ? "Expires today" : `Expired ${ago} day${ago === 1 ? "" : "s"} ago`;
  }
  if (daysRemaining >= 14) {
    const weeks = Math.round(daysRemaining / 7);
    return `${weeks} week${weeks === 1 ? "" : "s"} remaining`;
  }
  return `${daysRemaining} day${daysRemaining === 1 ? "" : "s"} remaining`;
}

export const expiryTone = (daysRemaining) =>
  daysRemaining == null ? "" : daysRemaining <= 0 ? "is-expired" : daysRemaining <= 7 ? "is-soon" : "is-ok";

export function LinkExpiryChip({ daysRemaining, testId }) {
  const text = fmtRemaining(daysRemaining);
  if (!text) return null;
  return (
    <span className={`rdd-expiry-chip ${expiryTone(daysRemaining)}`} title="How long the live link stays open for the prospect" data-testid={testId}>
      <Hourglass size={10} /> {text}
    </span>
  );
}

export function RefreshLinkButton({ proposalId, onExtended, compact, testId }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const extend = async (weeks, label) => {
    setBusy(weeks);
    try {
      const r = await axios.post(`${API}/api/proposals/${proposalId}/extend`, { weeks });
      toast.success(`Link refreshed — live for another ${label}`);
      onExtended?.(r.data);
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not refresh the link");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rdd-refresh" ref={ref}>
      <button type="button" className={`rdd-action ${compact ? "rdd-action-compact" : ""}`} onClick={() => setOpen((v) => !v)} title="Give the prospect more time — the link expires from today plus the period you pick" data-testid={testId}>
        <RefreshCw size={13} /> {compact ? "Refresh" : "Refresh link"}
      </button>
      {open && (
        <div className="rdd-refresh-menu" role="menu" data-testid={`${testId}-menu`}>
          <span className="rdd-refresh-title">Keep the link live for…</span>
          {EXTEND_CHOICES.map((c) => (
            <button key={c.weeks} type="button" role="menuitem" className="rdd-refresh-choice" disabled={busy != null} onClick={() => extend(c.weeks, c.label)} data-testid={`${testId}-${c.weeks}w`}>
              {busy === c.weeks ? <Loader2 size={12} className="rdd-spin" /> : <span className="rdd-refresh-dot" />} {c.label}
              <em>from today</em>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
